import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Button } from "./ui/Button";
import { Field, Input, Select } from "./ui/Input";
import type { RequestField, RequestFieldScope } from "../lib/types";

// Inline field manager rendered inside a request modal so admins
// (Owner / L1) can add, rename, retype, flag required, or remove
// fields without leaving the form. Every change is persisted
// immediately and the shared query for the scope is invalidated so
// every user's form re-renders with the new catalogue. Shared
// between the Training and Time-off request modals.
export function InlineRequestFieldManager({
  scope, fields, onChanged,
}: {
  scope: RequestFieldScope;
  fields: RequestField[];
  // Called whenever the field list changes in a way that the open
  // draft's `metadata` should follow — a rename moves a value to the
  // new key so the user's typed input isn't silently lost, a delete
  // drops the value, and an add seeds an empty value. The updater
  // receives the current metadata object and returns the next one.
  onChanged: (
    updateMetadata: (
      prev: Record<string, string | number | boolean | null>,
    ) => Record<string, string | number | boolean | null>,
  ) => void;
}) {
  const qc = useQueryClient();
  // Two-step add flow: first choose a type, then configure it.
  // `null` → nothing being added. `{ step: "pick" }` → the big
  // type-picker card is open and the config form is hidden. Once a
  // type is picked the state moves to `{ step: "config", ... }` and
  // the type-specific config fields appear.
  const [adding, setAdding] = useState<
    | null
    | { step: "pick" }
    | {
        step: "config";
        label: string;
        fieldType: RequestField["fieldType"];
        required: boolean;
        // Picklist values for a dropdown field. Empty for every
        // other type; the save mutation also strips options when
        // the type isn't dropdown.
        options: string[];
      }
  >(null);
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["requestFields", scope] });
  }

  const updateMut = useMutation({
    mutationFn: (args: { id: string; patch: Partial<RequestField>; priorKey: string }) =>
      api.updateRequestField(args.id, args.patch).then((r) => ({ r, priorKey: args.priorKey })),
    onSuccess: ({ r, priorKey }) => {
      if (r.key !== priorKey) {
        onChanged((prev) => {
          const { [priorKey]: carry, ...rest } = prev;
          return { ...rest, [r.key]: carry ?? "" };
        });
      }
      invalidate();
      setError(null);
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (args: { id: string; key: string }) =>
      api.deleteRequestField(args.id).then(() => args.key),
    onSuccess: (key) => {
      onChanged((prev) => {
        const { [key]: _gone, ...rest } = prev;
        return rest;
      });
      invalidate();
      setError(null);
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });
  const createMut = useMutation({
    mutationFn: (d: {
      label: string;
      fieldType: RequestField["fieldType"];
      required: boolean;
      options?: string[];
    }) =>
      api.createRequestField({
        scope,
        label: d.label,
        key: keyFromLabel(d.label),
        fieldType: d.fieldType,
        required: d.required,
        options: d.options,
        sortOrder: (fields[fields.length - 1]?.sortOrder ?? 0) + 10,
      }),
    onSuccess: (row) => {
      onChanged((prev) => ({
        ...prev,
        [row.key]: row.fieldType === "checkbox" ? false : "",
      }));
      invalidate();
      setAdding(null);
      setError(null);
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });

  return (
    <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded text-sm space-y-2">
      <div className="text-xs text-slate-500">
        Changes apply immediately and to every officer's request form.
      </div>
      {error && <div className="text-xs text-red-700">{error}</div>}

      <div className="space-y-1">
        {fields.map((f) => (
          <FieldRow
            key={f.id}
            field={f}
            onSave={(patch) => updateMut.mutate({ id: f.id, patch, priorKey: f.key })}
            onDelete={() => deleteMut.mutate({ id: f.id, key: f.key })}
          />
        ))}
        {fields.length === 0 && (
          <div className="text-xs text-slate-500 italic">No fields yet.</div>
        )}
      </div>

      {!adding && (
        <button
          type="button"
          className="text-xs font-medium text-sky-700 hover:text-sky-900"
          onClick={() => setAdding({ step: "pick" })}
        >
          + Add field
        </button>
      )}
      {adding?.step === "pick" && (
        <div className="pt-2 border-t border-slate-200 space-y-2">
          <div className="text-xs font-medium text-slate-700">
            What type of field do you want to add?
          </div>
          <div className="grid grid-cols-2 gap-2">
            {FIELD_TYPE_OPTIONS.map((opt) => {
              // Date & time is a fixed, label-less built-in on the
              // form — one per form — so if it's already present the
              // card goes to a disabled "Already added" state.
              const alreadyExists = opt.fixedLabel
                && fields.some((f) => f.fieldType === opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  disabled={alreadyExists || createMut.isPending}
                  onClick={() => {
                    if (opt.fixedLabel) {
                      // Skip the config step entirely — add immediately
                      // with the built-in label and default options.
                      createMut.mutate({
                        label: opt.fixedLabel,
                        fieldType: opt.id,
                        required: false,
                      });
                    } else {
                      setAdding({
                        step: "config",
                        fieldType: opt.id,
                        label: "",
                        required: false,
                        options: [],
                      });
                    }
                  }}
                  className={
                    "text-left rounded border p-2 "
                    + (alreadyExists
                      ? "border-slate-200 bg-slate-100 opacity-60 cursor-not-allowed"
                      : "border-slate-200 bg-white hover:border-sky-400 hover:bg-sky-50")
                  }
                >
                  <div className="text-xs font-semibold text-slate-800">
                    {opt.label}
                    {alreadyExists && (
                      <span className="ml-1.5 text-[10px] font-normal text-slate-500">
                        · already added
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{opt.hint}</div>
                </button>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" onClick={() => setAdding(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {adding?.step === "config" && (
        <div className="pt-2 border-t border-slate-200 space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>
              Adding a{" "}
              <strong>
                {FIELD_TYPE_LABEL[adding.fieldType] ?? adding.fieldType}
              </strong>{" "}
              field.
            </span>
            <button
              type="button"
              className="text-[11px] text-sky-700 hover:underline"
              onClick={() => setAdding({ step: "pick" })}
            >
              Change type
            </button>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Label">
              <Input
                autoFocus
                value={adding.label}
                onChange={(e) => setAdding({ ...adding, label: e.target.value })}
                placeholder={FIELD_TYPE_PLACEHOLDER[adding.fieldType] ?? "e.g. Label"}
              />
            </Field>
            {adding.fieldType !== "checkbox" && (
              // Required flag doesn't carry over to checkboxes — a
              // required checkbox would just mean "must be ticked",
              // which the submitter can't tell apart from a stuck
              // input. Keep the toggle visible only for the inputs
              // where "did they fill it in" is a meaningful check.
              <label className="flex items-center gap-1 text-xs text-slate-700 mb-2">
                <input
                  type="checkbox"
                  className="h-3 w-3"
                  checked={adding.required}
                  onChange={(e) => setAdding({ ...adding, required: e.target.checked })}
                />
                Required
              </label>
            )}
          </div>
          {adding.fieldType === "dropdown" && (
            <DropdownOptionsEditor
              options={adding.options}
              onChange={(next) => setAdding({ ...adding, options: next })}
            />
          )}
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              disabled={
                !adding.label.trim()
                || createMut.isPending
                || (adding.fieldType === "dropdown"
                  && adding.options.filter((o) => o.trim()).length < 2)
              }
              onClick={() => createMut.mutate({
                label: adding.label,
                fieldType: adding.fieldType,
                required: adding.required,
                options: adding.fieldType === "dropdown" ? adding.options : undefined,
              })}
              title={
                adding.fieldType === "dropdown"
                && adding.options.filter((o) => o.trim()).length < 2
                  ? "Add at least two options before saving."
                  : undefined
              }
            >
              {createMut.isPending ? "Adding…" : "Add"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Field types the user can pick from the "Add field" picker. The
// id here MUST match the DB CHECK (migrations 0059 / 0060). Hint
// is one short sentence shown under the type name in the picker
// so an admin new to the tool knows what each one does.
// `fixedLabel` turns the card into a one-click add: the picker
// bypasses the config step and creates the field immediately with
// that label (one per form). Date & time uses this because it's
// the same compound widget on every form — the admin doesn't
// customise anything about it beyond "is it on this form or not".
const FIELD_TYPE_OPTIONS: Array<{
  id: RequestField["fieldType"];
  label: string;
  hint: string;
  fixedLabel?: string;
}> = [
  { id: "text", label: "Text", hint: "Single-line free-form text." },
  { id: "number", label: "Number", hint: "A numeric value — hours, miles, count." },
  { id: "checkbox", label: "Checkbox", hint: "Yes / no toggle." },
  {
    id: "date_time",
    label: "Date & time",
    hint: "Date picker + time picker (same as the Time off form). Adds instantly — no label needed.",
    fixedLabel: "Date & time",
  },
  { id: "dropdown", label: "Dropdown", hint: "Pick one from a list you define." },
  {
    id: "comp_hours",
    label: "Comp hours",
    hint: "Hours added to the officer's Comp (C) bank in Records on approval.",
    fixedLabel: "Comp hours",
  },
];
const FIELD_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  FIELD_TYPE_OPTIONS.map((o) => [o.id, o.label]),
);
const FIELD_TYPE_PLACEHOLDER: Record<string, string> = {
  text: "e.g. Course Title",
  number: "e.g. Hours",
  checkbox: "e.g. Includes vehicle",
  date_time: "e.g. Course date & start time",
  dropdown: "e.g. Course location",
  comp_hours: "Comp hours",
};

function FieldRow({
  field, onSave, onDelete,
}: {
  field: RequestField;
  onSave: (patch: Partial<RequestField>) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(field.label);
  const [fieldType, setFieldType] = useState<RequestField["fieldType"]>(field.fieldType);
  const [required, setRequired] = useState(field.required);
  const [options, setOptions] = useState<string[]>(field.options ?? []);
  const [armedDelete, setArmedDelete] = useState(false);

  if (!editing) {
    return (
      <div className="flex items-center gap-2 py-1">
        <span className="flex-1 truncate">
          <span className="font-medium">{field.label}</span>
          <span className="ml-2 text-xs text-slate-500 capitalize">
            ({field.fieldType}{field.required ? ", required" : ""})
          </span>
        </span>
        <button
          type="button"
          className="text-xs text-sky-700 hover:text-sky-900"
          onClick={() => setEditing(true)}
        >
          Edit
        </button>
        {armedDelete ? (
          <button
            type="button"
            className="text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded px-2 py-0.5"
            onClick={() => { setArmedDelete(false); onDelete(); }}
          >
            Confirm delete
          </button>
        ) : (
          <button
            type="button"
            className="text-xs text-red-700 hover:text-red-900"
            onClick={() => setArmedDelete(true)}
          >
            Delete
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-2 py-1">
      <Field label="Label">
        <Input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>
      <Field label="Type">
        <Select
          value={fieldType}
          onChange={(e) =>
            setFieldType(e.target.value as RequestField["fieldType"])}
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="checkbox">Checkbox</option>
          <option value="date_time">Date &amp; time</option>
          <option value="dropdown">Dropdown</option>
          <option value="comp_hours">Comp hours</option>
          {/* Legacy standalone date / time — kept selectable only
            * when the field in question already uses them so the
            * admin can save without an "invalid type" bounce. New
            * fields always get the compound Date & time instead. */}
          {field.fieldType === "date" && <option value="date">Date (legacy)</option>}
          {field.fieldType === "time" && <option value="time">Time (legacy)</option>}
        </Select>
      </Field>
      {fieldType !== "checkbox" && (
        <label className="flex items-center gap-1 text-xs text-slate-700 mb-2">
          <input
            type="checkbox"
            className="h-3 w-3"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          Required
        </label>
      )}
      {fieldType === "dropdown" && (
        <div className="w-full">
          <DropdownOptionsEditor options={options} onChange={setOptions} />
        </div>
      )}
      <Button
        size="sm"
        disabled={
          !label.trim()
          || (fieldType === "dropdown"
            && options.filter((o) => o.trim()).length < 2)
        }
        onClick={() => {
          const renamed = label.trim() !== field.label;
          onSave({
            label: label.trim(),
            fieldType,
            required,
            // Only send options when the saved field is a dropdown;
            // the api layer strips them for other types anyway, but
            // keeping the patch tight avoids unnecessary writes.
            ...(fieldType === "dropdown" ? { options } : {}),
            // Renaming the label syncs the stable `key` too so the
            // admin isn't stuck with a stale snake_case identifier.
            ...(renamed ? { key: keyFromLabel(label) } : {}),
          });
          setEditing(false);
        }}
      >
        Save
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </div>
  );
}

// Simple editable list of picklist values for a dropdown field.
// Each row is a text input + remove button; an "+ Add option"
// link appends an empty row the admin can type into. Options are
// trimmed and empty rows are dropped on save by the api layer.
function DropdownOptionsEditor({
  options, onChange,
}: {
  options: string[];
  onChange: (next: string[]) => void;
}) {
  function update(i: number, v: string) {
    const next = options.slice();
    next[i] = v;
    onChange(next);
  }
  function remove(i: number) {
    onChange(options.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...options, ""]);
  }
  return (
    <div className="mt-1 p-2 bg-white border border-slate-200 rounded space-y-1.5">
      <div className="text-[11px] font-medium text-slate-600">
        Options the submitter can pick from
      </div>
      {options.length === 0 && (
        <div className="text-[11px] italic text-slate-400">
          No options yet. Add at least two.
        </div>
      )}
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={opt}
            onChange={(e) => update(i, e.target.value)}
            placeholder={`Option ${i + 1}`}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-slate-400 hover:text-red-600 text-sm px-1"
            title="Remove this option"
            aria-label="Remove option"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-xs text-sky-700 hover:text-sky-900 font-medium"
      >
        + Add option
      </button>
    </div>
  );
}

// Renders a list of request fields with one field per row, except
// that consecutive checkbox fields share a wrapping row so an
// admin can line up a cluster of yes/no toggles side-by-side.
// Each non-checkbox field (text, number, dropdown, date_time) sits
// on its own full-width line. All three request surfaces (Forms,
// Time off, Training) call this so the layout stays identical.
export function RequestFieldList({
  fields, values, onChange,
}: {
  fields: RequestField[];
  values: Record<string, string | number | boolean | null | undefined>;
  onChange: (key: string, v: string | number | boolean) => void;
}) {
  const sorted = fields.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  // Group consecutive checkboxes together. Everything else is its
  // own single-field block that renders full width.
  type Block =
    | { kind: "field"; field: RequestField }
    | { kind: "checkboxes"; fields: RequestField[] };
  const blocks: Block[] = [];
  for (const f of sorted) {
    if (f.fieldType === "checkbox") {
      const last = blocks[blocks.length - 1];
      if (last && last.kind === "checkboxes") {
        last.fields.push(f);
      } else {
        blocks.push({ kind: "checkboxes", fields: [f] });
      }
    } else {
      blocks.push({ kind: "field", field: f });
    }
  }
  return (
    <div className="space-y-3">
      {blocks.map((b, i) => {
        if (b.kind === "field") {
          return (
            <div key={b.field.id}>
              <RequestFieldInput
                field={b.field}
                value={values[b.field.key]}
                onChange={(v) => onChange(b.field.key, v)}
              />
            </div>
          );
        }
        return (
          <div key={`cb:${i}`} className="flex flex-wrap gap-x-6 gap-y-2">
            {b.fields.map((f) => (
              <RequestFieldInput
                key={f.id}
                field={f}
                value={values[f.key]}
                onChange={(v) => onChange(f.key, v)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function keyFromLabel(label: string): string {
  return label.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Shared read-side formatter for request-field values. Called from
// every receipt / list / print surface so a `date_time` field
// renders as "Oct 3, 2026 • 2:30 PM" and the legacy standalone
// date / time types render in locale form, instead of raw ISO
// strings. Checkbox / number / text fall back to String(v).
export function formatRequestFieldValue(
  field: RequestField,
  value: string | number | boolean | null | undefined,
): string {
  if (value === undefined || value === null || value === "") return "";
  if (field.fieldType === "checkbox") return value ? "Yes" : "No";
  if (field.fieldType === "date_time") {
    const r = parseDateTimeRange(String(value));
    const sd = formatDatePart(r.startDate);
    const ed = formatDatePart(r.endDate);
    const st = formatTimePart(r.startTime);
    const et = formatTimePart(r.endTime);
    const dates =
      sd && ed && sd !== ed ? `${sd} → ${ed}`
      : sd || ed || "";
    const times =
      st && et ? `${st} → ${et}`
      : st || et || "";
    if (dates && times) return `${dates} • ${times}`;
    return dates || times || String(value);
  }
  if (field.fieldType === "date") return formatDatePart(String(value)) || String(value);
  if (field.fieldType === "time") return formatTimePart(String(value)) || String(value);
  if (field.fieldType === "comp_hours") {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return `${n % 1 === 0 ? n : n.toFixed(2)} h → Comp`;
  }
  return String(value);
}

// Split a "YYYY-MM-DD HH:MM" (or just "YYYY-MM-DD" / "HH:MM") into
// its date and time halves. Tolerant of partial input while the
// user is still typing. Kept for pre-range date_time values.
export function splitDateTime(raw: string): [string, string] {
  const s = raw.trim();
  if (!s) return ["", ""];
  // Full "YYYY-MM-DD HH:MM" (or with "T" between — datetime-local).
  const full = /^(\d{4}-\d{2}-\d{2})[ T](\d{1,2}:\d{2})$/.exec(s);
  if (full) return [full[1], full[2]];
  // Date-only.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return [s, ""];
  // Time-only.
  if (/^\d{1,2}:\d{2}$/.test(s)) return ["", s];
  return [s, ""];
}

// Parse a date_time field's stored value into its four parts.
// Current format: "startDate|endDate|startTime|endTime" with any
// part allowed to be empty. Falls back to the pre-range format
// ("YYYY-MM-DD HH:MM") so values saved against earlier builds
// still render correctly after this change.
export function parseDateTimeRange(raw: string): {
  startDate: string; endDate: string;
  startTime: string; endTime: string;
} {
  const s = (raw ?? "").trim();
  if (!s) return { startDate: "", endDate: "", startTime: "", endTime: "" };
  if (s.includes("|")) {
    const [sd = "", ed = "", st = "", et = ""] = s.split("|");
    return {
      startDate: sd.trim(),
      endDate: ed.trim(),
      startTime: st.trim(),
      endTime: et.trim(),
    };
  }
  // Pre-range value — map to startDate + startTime.
  const [d, t] = splitDateTime(s);
  return { startDate: d, endDate: "", startTime: t, endTime: "" };
}

function formatDatePart(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return "";
  try {
    // Local-date parsing (Date(y,m-1,d)) so a Sun-stored date
    // never reads as Saturday in a westward tz.
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return ""; }
}

function formatTimePart(s: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return "";
  try {
    const d = new Date();
    d.setHours(Number(m[1]), Number(m[2]), 0, 0);
    return d.toLocaleTimeString(undefined, {
      hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
}

// One input per request_field, driven by the field's type. The
// value shape (string for text/number, boolean for checkbox) is
// what gets stored into time_off.metadata.
export function RequestFieldInput({
  field, value, onChange,
}: {
  field: RequestField;
  value: string | number | boolean | null | undefined;
  onChange: (v: string | number | boolean) => void;
}) {
  if (field.fieldType === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm text-slate-700 mt-4">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        {field.label}
        {field.required && <span className="text-amber-600">*</span>}
      </label>
    );
  }
  const label = field.label + (field.required ? " *" : "");
  if (field.fieldType === "number") {
    return (
      <Field label={label}>
        <Input
          type="number"
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
    );
  }
  if (field.fieldType === "dropdown") {
    const opts = field.options ?? [];
    const current = value === null || value === undefined ? "" : String(value);
    return (
      <Field label={label}>
        <Select
          value={current}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Select —</option>
          {opts.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </Select>
      </Field>
    );
  }
  if (field.fieldType === "comp_hours") {
    return (
      <Field label={label}>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            step={0.25}
            min={0}
            inputMode="decimal"
            value={value === null || value === undefined ? "" : String(value)}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1"
            placeholder="0"
          />
          <span className="text-xs font-medium text-slate-500 whitespace-nowrap">
            hours → Comp bank
          </span>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          On approval this adds to your Comp (C) balance in Records.
        </p>
      </Field>
    );
  }
  if (field.fieldType === "date_time") {
    // Compound "Date & time" field — four pickers in a 2×2 grid:
    // Start date / End date on top, From / To times underneath, so
    // the submitter can pick an entire window at once (same shape
    // as the Time off form, minus the "Only part of the day"
    // toggle — the time inputs are always visible). Stored as a
    // single pipe-delimited string
    //     "startDate|endDate|startTime|endTime"
    // so the metadata column can keep the value as a plain string
    // like every other field type; any missing part is kept empty.
    const parts = parseDateTimeRange(
      value === null || value === undefined ? "" : String(value),
    );
    function emit(next: {
      startDate?: string; endDate?: string;
      startTime?: string; endTime?: string;
    }) {
      const merged = { ...parts, ...next };
      const anyFilled =
        merged.startDate || merged.endDate
        || merged.startTime || merged.endTime;
      if (!anyFilled) { onChange(""); return; }
      onChange([
        merged.startDate ?? "",
        merged.endDate ?? "",
        merged.startTime ?? "",
        merged.endTime ?? "",
      ].join("|"));
    }
    return (
      <div className="space-y-2">
        {field.label && (
          <div className="text-xs font-medium text-slate-600">
            {label}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Field label="Start date">
            <Input
              type="date"
              value={parts.startDate}
              onChange={(e) => emit({ startDate: e.target.value })}
            />
          </Field>
          <Field label="End date">
            <Input
              type="date"
              value={parts.endDate}
              onChange={(e) => emit({ endDate: e.target.value })}
            />
          </Field>
          <Field label="From">
            <Input
              type="time"
              value={parts.startTime}
              onChange={(e) => emit({ startTime: e.target.value })}
            />
          </Field>
          <Field label="To">
            <Input
              type="time"
              value={parts.endTime}
              onChange={(e) => emit({ endTime: e.target.value })}
            />
          </Field>
        </div>
      </div>
    );
  }
  // Legacy standalone date / time — kept so pre-compound fields
  // still render correctly; add dropdown doesn't offer these any
  // more.
  if (field.fieldType === "date") {
    return (
      <Field label={label}>
        <Input
          type="date"
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
    );
  }
  if (field.fieldType === "time") {
    return (
      <Field label={label}>
        <Input
          type="time"
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
    );
  }
  return (
    <Field label={label}>
      <Input
        value={value === null || value === undefined ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}
